import { useRef, useState, useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import SectionDivider from './_sectionDivider';
import IndicativeRegulatoryReport from './_indicativeRegulatoryReport';
import { AddressDto, KosmosReportDto, PdfStyleDto, ReportTypeDto } from 'models/dtoTypes';
import { GetCompanyKosmosReportById } from 'services/kosmosReport';
import Section from './_section';
import SdgReport from './_sdgReport';
import DualUseGoodsReport from './_dualUseGoodsReport';
import DualUseServicesReport from './_dualUseServicesReport';
import IllegalLoggingGoodsReport from './_illegalLoggingReport';
import DoNoSignificantHarmDetailReport from './_doNoSignificantHarmDetailReport';
import EuTaxonomyReport from './_euTaxonomyReport';
import SustainableFinanceDisclosureRegulationReport from './_sustainableFinanceDisclosureRegulationReport';
import GreenHouseGasReport from './_greenHouseGasReport';
import NewsCheckReport from './_newsCheckReport';
import GovernanceReport from './_governanceReport';
import CapexOpexReport from './_capexOpexReport';
// import CompanyPolicySubmissionReport from './_companyPolicySubmissionReport';
// import FinalOverviewReport from './_finalOverviewReport';
import Appendix from './_appendix';
import TopActionBar from './_topActionBar';
import PageHeader from './_pageHeader';
import { LoadingRoundButton, RoundButton } from './_colorButton';
import { useTranslation } from 'react-i18next';
import downloadKosmosReport from 'utils/downloadKosmosReport';
import ModernSlaveryReport from './_modernSlaveryReport';

const PREFIX = 'CompanyReport';

const classes = {
  tabHeaders: `${PREFIX}-tabHeaders`,
  tabRoot: `${PREFIX}-tabRoot`,
  tabSelected: `${PREFIX}-tabSelected`,
  links: `${PREFIX}-links`,
  linkContainer: `${PREFIX}-linkContainer`,
  floatBtnContainer: `${PREFIX}-floatBtnContainer`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  fontWeight: 'regular',
  padding: theme.spacing(6),
  [`& .${classes.tabHeaders}`]: {},
  [`& .${classes.tabRoot}`]: {
    height: 70,
    fontSize: 20,
  },
  [`& .${classes.tabSelected}`]: {
    fontWeight: 'bold',
  },
  [`& .${classes.links}`]: {
    display: 'flex',
  },
  [`& .${classes.linkContainer}`]: {
    flex: 1,
  },
  [`& .${classes.floatBtnContainer}`]: {
    position: 'fixed',
    display: 'flex',
    right: theme.spacing(6),
    bottom: theme.spacing(6),
    zIndex: 1001,
  },
}));

interface IProps {
  id: string;
  organisationId?: string;
  onBack?: () => void;
}

export default function CompanyReport(props: IProps): JSX.Element {
  const { id, organisationId, onBack } = props;
  const [loading, setLoading] = useState(false);
  const [kosmosReport, setKosmosReport] = useState<KosmosReportDto>();
  const elementRef = useRef(null);
  const { t } = useTranslation('default');

  const handleSave = async (): Promise<void> => {
    const reportDate = kosmosReport?.reportMetadata?.reportDate || id;
    const name = kosmosReport?.companyMetadata?.name || 'name unidentified';
    const filename = `${name} - Coriolis Sustainability Report -${reportDate}.pdf`;
    await downloadKosmosReport({
      companyId: id,
      organisationId,
      defaultFilename: filename,
      pdfStyle: PdfStyleDto.CoriolisEsg,
      reportType: ReportTypeDto.Detailed,
    });
  };

  useEffect(() => {
    if (!id) {
      return;
    }
    const fetchKosmosReport = async () => {
      setLoading(true);
      try {
        const report = await GetCompanyKosmosReportById(id, organisationId);
        setKosmosReport(report);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    void fetchKosmosReport();
  }, [id]);

  const generateFullAddress = (address?: AddressDto): string => {
    if (!address) {
      return t('tnknown');
    }
    const { addressLine1, addressLine2, city, country, postalCode } = address;
    const addrComponents = [addressLine1, addressLine2, city, country, postalCode];
    return addrComponents.filter((c) => c).join(', ');
  };

  const indicativeRegulatoryReport = useMemo(() => {
    const {
      name,
      companyAddress,
      companyAddressInFull,
      companyRegistrationNumber,
      companyRegistrationNumberDescriptor,
    } = kosmosReport?.companyMetadata || {};
    const {
      kosmosEsgScore,
      kosmosEsgEnvironmentScore,
      kosmosEsgSocialScore,
      kosmosEsgGovernanceScore,
      reportDate,
      isSmartGovVerified,
    } = kosmosReport?.reportMetadata || {};
    return {
      name: name || '',
      address: companyAddressInFull || generateFullAddress(companyAddress),
      companyRegistrationNumber: companyRegistrationNumber || '',
      companyRegistrationNumberDescriptor: companyRegistrationNumberDescriptor || '',
      reportNumber: kosmosReport?.reportNumber || '',
      reportDate: reportDate || '',
      score: kosmosEsgScore || 0,
      kosmosEsgEnvironmentScore: kosmosEsgEnvironmentScore || 0,
      kosmosEsgSocialScore: kosmosEsgSocialScore || 0,
      kosmosEsgGovernanceScore: kosmosEsgGovernanceScore || 0,
      isSmartGovVerified: isSmartGovVerified ?? false,
    };
  }, [kosmosReport]);

  const onScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  if (loading) {
    return (
      <Root>
        <CircularProgress />
      </Root>
    );
  }

  if (!kosmosReport) {
    return (
      <Root>
        <TopActionBar onBack={onBack} />
      </Root>
    );
  }

  const {
    adverseNewsReport,
    sdgReport,
    dualUseGoodsReport,
    dualUseServicesReport,
    modernSlaveryReport,
    illegalLoggingReport,
    doesNoSignificantHarmDetailReport,
    euTaxonomyReport,
    sustainableFinanceDisclosureRegulationReport,
    governanceReport,
    ghgScopeReport,
    ghgScopeSupplyChainAppendixReport,
  } = kosmosReport;
  const capexOpexReport = kosmosReport.capexOpexReport || [];
  // <DualUseGoodsReport dualUseGoodsReport={kosmosReport?.dualUseGoodsReport || []}/>
  // <DualUseGoodsReport dualUseGoodsReport={kosmosReport?.dualUseGoodsReport || []}/>
  // <DualUseGoodsReport dualUseGoodsReport={kosmosReport?.dualUseGoodsReport || []}/>
  return (
    <Root ref={elementRef}>
      <div className="no-print">
        <TopActionBar onBack={onBack} id={id} />
        <PageHeader
          kosmosCompanyId={id}
          name={indicativeRegulatoryReport.name}
          address={indicativeRegulatoryReport.address}
          isSmartGovVerified={indicativeRegulatoryReport.isSmartGovVerified}
        />
      </div>

      <Section title={t('indicative-regulatory-report')}>
        <IndicativeRegulatoryReport
          name={indicativeRegulatoryReport.name}
          address={indicativeRegulatoryReport.address}
          companyRegistrationNumber={indicativeRegulatoryReport.companyRegistrationNumber}
          companyRegistrationNumberDescriptor={
            indicativeRegulatoryReport.companyRegistrationNumberDescriptor
          }
          overallScore={indicativeRegulatoryReport.score}
          kosmosEsgEnvironmentScore={indicativeRegulatoryReport.kosmosEsgEnvironmentScore}
          kosmosEsgSocialScore={indicativeRegulatoryReport.kosmosEsgSocialScore}
          kosmosEsgGovernanceScore={indicativeRegulatoryReport.kosmosEsgGovernanceScore}
          reportId={indicativeRegulatoryReport.reportNumber}
          reportDate={indicativeRegulatoryReport.reportDate}
        />
      </Section>

      <Section title={t('an-automated-check-of-this-company-has-found')}>
        <>
          <SectionDivider id="sustainable-development-goals" />
          <SdgReport sdgReport={sdgReport || []} />
          <SectionDivider id="dual-use-goods-check" />
          <DualUseGoodsReport dualUseGoodsReport={dualUseGoodsReport || []} />
          <SectionDivider id="dual-use-services-check" />
          <DualUseServicesReport dualUseServicesReport={dualUseServicesReport || []} />
          <SectionDivider id="modern-slavery" />
          <ModernSlaveryReport modernSlaveryReport={modernSlaveryReport || []} />
          <SectionDivider id="illegal-logging-check" />
          <IllegalLoggingGoodsReport illegalLoggingReport={illegalLoggingReport || []} />
          <SectionDivider id="eu-taxonomy-score" />
          <EuTaxonomyReport euTaxonomyReport={euTaxonomyReport || []} />
          <SectionDivider id="eu-taxonomy-regulation-dhsh" />
          <DoNoSignificantHarmDetailReport
            doNoSignificantHarmDetailReport={doesNoSignificantHarmDetailReport || []}
          />
          <SectionDivider id="sustainable-finance-disclosure-regulation" />
          <SustainableFinanceDisclosureRegulationReport
            sustainableFinanceDisclosureRegulationReport={
              sustainableFinanceDisclosureRegulationReport || []
            }
          />
          <SectionDivider id="greenhouse-gases" />
          <GreenHouseGasReport ghgScopeReport={ghgScopeReport || []} />
          <SectionDivider id="news-check" />
          <NewsCheckReport
            adverseNewsReport={adverseNewsReport || []}
            enableBarChart={true}
            enableTrendChart={false}
          />
          <SectionDivider id="governance" />
          <GovernanceReport
            negativeGovernance={governanceReport?.NegativeGovernance || []}
            positiveGovernance={governanceReport?.PositiveGovernance || []}
          />
        </>
      </Section>
      {capexOpexReport.length > 0 && (
        <Section id="self-reported-information" title={t('self-reported-information')}>
          <CapexOpexReport capexOpexReport={capexOpexReport} />
        </Section>
      )}

      {
        //  <CompanyPolicySubmissionReport companyPolicySubmissionReport={companyPolicySubmissionReport || []} />
        //  <FinalOverviewReport
        //    name={indicativeRegulatoryReport.name}
        //    score={indicativeRegulatoryReport.score}
        //  />
      }
      <div id="appendix">
        <Appendix
          sdgReport={sdgReport || []}
          ghgScopeSupplyChainAppendixReport={ghgScopeSupplyChainAppendixReport || []}
        />
      </div>
      <div className={classes.floatBtnContainer}>
        <LoadingRoundButton onClick={handleSave} />
        <RoundButton onClick={onScrollToTop} />
      </div>
    </Root>
  );
}
